/**
 * 本地登录凭证存储 (记住我 / 自动重登复用)
 *
 * 用途:
 *   1. 登录页"记住我" — 勾选后把账号密码存本地, 下次打开自动回填表单.
 *   2. request.js 401 无感自动重登 — token 过期时用本地凭证静默换新 token, 用户无需重新登录.
 *
 * 编解码保持与 Login.vue 旧版内联逻辑完全一致 (btoa + UTF-8 转义),
 * 以兼容已经存在 localStorage 的 remember_credentials 数据.
 *
 * 安全性说明:
 *   base64 仅"防一眼可见", 不是加密. 真正的安全靠 HTTPS + 后端短 token TTL + 账号密码本身.
 *   本模块只做存储读写, 不涉及密码加密强度判断.
 */

const KEY = 'remember_credentials'

/**
 * 保存凭证到本地 (覆盖式)
 * @param {string} username
 * @param {string} password  明文 (与登录提交一致的明文)
 */
export function saveCredentials(username, password) {
  try {
    const payload = JSON.stringify({
      u: username,
      // btoa 不支持非 ASCII, 用 unescape/encodeURIComponent 转 UTF-8 字节再编码 (与旧版一致)
      p: btoa(unescape(encodeURIComponent(password)))
    })
    localStorage.setItem(KEY, payload)
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[credentialStore] saveCredentials failed', e)
  }
}

/**
 * 读取并解码本地凭证
 * @returns {{username: string, password: string} | null} 无缓存或损坏返回 null
 */
export function loadCredentials() {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    const username = parsed.u || ''
    const password = parsed.p ? decodeURIComponent(escape(atob(parsed.p))) : ''
    if (!username || !password) return null
    return { username, password }
  } catch (e) {
    // 损坏的 cache 忽略, 视为无凭证
    return null
  }
}

/**
 * 是否存在可用凭证 (loadCredentials 的轻量判断, 自动重登前先调它)
 * @returns {boolean}
 */
export function hasCredentials() {
  return loadCredentials() !== null
}

/**
 * 清除本地凭证 (登出 / 自动重登失败时调用)
 */
export function clearCredentials() {
  try {
    localStorage.removeItem(KEY)
  } catch (e) {
    /* ignore */
  }
}

// 兼容: 清掉旧版只记 username 的 key (v0.2 之前), 与 Login.vue 旧逻辑保持一致
export function clearLegacyRememberUser() {
  try {
    localStorage.removeItem('remember_user')
  } catch (e) {
    /* ignore */
  }
}
